// src/pages/checkin/Scanner.jsx
// Scanner de check-in. Acesso via sessionCode gerado no dashboard.
// Fluxo: câmera -> lê QR -> busca ticket -> mostra info -> validar / cancelar.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import QrCodeScannerRoundedIcon from "@mui/icons-material/QrCodeScannerRounded";
import KeyboardRoundedIcon from "@mui/icons-material/KeyboardRounded";
import ConfirmationNumberRoundedIcon from "@mui/icons-material/ConfirmationNumberRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import { Html5Qrcode } from "html5-qrcode";
import { get, ref, runTransaction } from "firebase/database";
import { rtdb } from "../../services/firebase/rtdb";
import { useAuthContext } from "../../contexts/AuthContext";
import { auth } from "../../services/firebase/auth";

const bgSx = {
  minHeight: "100vh",
  background:
    "radial-gradient(1200px circle at 20% 10%, rgba(34,197,94,0.13), transparent 55%), " +
    "radial-gradient(900px circle at 80% 30%, rgba(59,130,246,0.10), transparent 55%), #070b14",
};

const glassSx = {
  p: { xs: 2, sm: 3 },
  borderRadius: "20px",
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.10)",
  backdropFilter: "blur(18px)",
};

const greenBtn = {
  background: "#22c55e",
  "&:hover": { background: "#16a34a" },
  borderRadius: "14px",
  fontWeight: 900,
  color: "#052e16",
};

const ghostBtn = {
  borderRadius: "14px",
  color: "rgba(255,255,255,0.88)",
  borderColor: "rgba(255,255,255,0.18)",
  fontWeight: 700,
};

const redBtn = {
  borderRadius: "14px",
  borderColor: "rgba(248,113,113,0.4)",
  color: "#fca5a5",
  fontWeight: 700,
  "&:hover": {
    borderColor: "#f87171",
    background: "rgba(248,113,113,0.08)",
  },
};

function fmt(n) {
  return Number(n || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function fmtDate(ms) {
  if (!ms) return "—";

  return new Date(Number(ms)).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Parse QR value: "ONURB|{ticketId}|{orderId}|{eventId}"
 * Retorna null se o formato não bater.
 */
function parseQR(raw) {
  if (!raw) return null;

  const parts = String(raw)
    .trim()
    .split("|")
    .map((part) => part.trim());

  if (parts.length < 4 || parts[0] !== "ONURB") return null;

  return {
    ticketId: parts[1],
    orderId: parts[2],
    eventId: parts[3],
  };
}

function isLocalCameraHost() {
  const host = window.location.hostname;
  return host === "localhost" || host === "127.0.0.1" || host === "::1";
}

function getCameraBootstrapError() {
  if (typeof window === "undefined") {
    return "Scanner indisponível fora do navegador.";
  }

  if (!window.isSecureContext && !isLocalCameraHost()) {
    return "A câmera só pode ser aberta em uma conexão segura (HTTPS). Se este link estiver em HTTP, abra a versão HTTPS do check-in.";
  }

  if (!navigator.mediaDevices?.getUserMedia) {
    return "Este navegador não disponibiliza acesso à câmera.";
  }

  return "";
}

function scoreCameraLabel(label = "") {
  const normalized = label.toLowerCase();
  let score = 0;

  if (/(back|rear|traseira|traseiro|ambiente|environment|world)/i.test(normalized)) {
    score += 100;
  }

  if (/(front|user|frontal|facetime|selfie)/i.test(normalized)) {
    score -= 100;
  }

  if (/(wide|ultra|main|principal)/i.test(normalized)) {
    score += 10;
  }

  return score;
}

async function getPreferredCameraCandidates() {
  try {
    const cameras = await Html5Qrcode.getCameras();
    return [...cameras].sort((a, b) => scoreCameraLabel(b.label) - scoreCameraLabel(a.label));
  } catch (err) {
    console.warn("Could not enumerate cameras:", err?.message ?? err);
    return [];
  }
}

function describeCameraError(err) {
  const message = String(err?.message ?? err ?? "").toLowerCase();

  if (message.includes("secure context") || message.includes("https")) {
    return "A câmera exige HTTPS para funcionar fora do localhost.";
  }

  if (
    message.includes("notallowederror") ||
    message.includes("permission") ||
    message.includes("denied") ||
    message.includes("permissão")
  ) {
    return "A permissão da câmera foi bloqueada no navegador.";
  }

  if (
    message.includes("notfounderror") ||
    message.includes("devicesnotfounderror") ||
    message.includes("no camera") ||
    message.includes("camera not found")
  ) {
    return "Nenhuma câmera disponível foi encontrada neste dispositivo.";
  }

  if (
    message.includes("notreadableerror") ||
    message.includes("trackstarterror") ||
    message.includes("could not start video source")
  ) {
    return "A câmera parece estar ocupada por outro app ou navegador.";
  }

  if (message.includes("overconstrainederror") || message.includes("constraint")) {
    return "O dispositivo recusou o modo de câmera solicitado. Vou tentar outros formatos automaticamente.";
  }

  return "Não foi possível acessar a câmera neste dispositivo.";
}

function createLogSnapshot(value, depth = 0) {
  if (value == null) return value;
  if (depth > 2) return "[max-depth]";

  if (value instanceof Error) {
    return {
      name: value.name,
      code: value.code,
      message: value.message,
      stack: value.stack,
    };
  }

  if (Array.isArray(value)) {
    return value.slice(0, 20).map((item) => createLogSnapshot(item, depth + 1));
  }

  if (typeof value === "object") {
    const out = {};
    Object.entries(value)
      .slice(0, 40)
      .forEach(([key, item]) => {
        out[key] = createLogSnapshot(item, depth + 1);
      });
    return out;
  }

  return value;
}

const DEV_TERMINAL_LOG_ENDPOINT = "/__dev/checkin-log";

export default function Scanner() {
  const { sessionCode } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, profile } = useAuthContext();

  const canValidateCheckin =
    isAuthenticated &&
    profile?.role === "organizer" &&
    profile?.approvalStatus === "approved";

  const [session, setSession] = useState(null);
  const [sessionError, setSessionError] = useState("");
  const [loadingSession, setLoadingSession] = useState(true);

  const [mode, setMode] = useState("camera");
  const [manualCode, setManualCode] = useState("");

  const [reviewing, setReviewing] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [fetchingTicket, setFetchingTicket] = useState(false);
  const [scanError, setScanError] = useState("");

  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [cameraError, setCameraError] = useState("");

  const regionId = useMemo(() => `qr-region-${sessionCode}`, [sessionCode]);
  const qrRef = useRef(null);
  const startedRef = useRef(false);
  const startingRef = useRef(false);
  const scannerRunIdRef = useRef(0);
  const activeScanRef = useRef(null);
  const lastScanRef = useRef("");
  const reviewingRef = useRef(false);
  const fileInputRef = useRef(null);
  const logCounterRef = useRef(0);
  const relayFailureLoggedRef = useRef(false);

  const logValidationStep = useCallback(
    (step, details = {}, level = "log") => {
      const index = ++logCounterRef.current;
      const entry = {
        index,
        at: new Date().toISOString(),
        sessionCode,
        step,
        details: createLogSnapshot(details),
      };

      window.__onurbCheckinLogs = window.__onurbCheckinLogs || [];
      window.__onurbCheckinLogs.push(entry);

      const printer = console[level] || console.log;
      printer(`[checkin][${index}] ${step}`, entry);

      if (
        process.env.NODE_ENV === "development" &&
        typeof window !== "undefined" &&
        typeof fetch === "function"
      ) {
        fetch(DEV_TERMINAL_LOG_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(entry),
          keepalive: true,
        }).catch((error) => {
          if (relayFailureLoggedRef.current) return;
          relayFailureLoggedRef.current = true;
          console.warn("[checkin] terminal log relay failed", error?.message ?? error);
        });
      }
    },
    [sessionCode]
  );

  useEffect(() => {
    logValidationStep("auth-context", {
      isAuthenticated,
      canValidateCheckin,
      profile: profile
        ? {
            uid: profile.uid,
            email: profile.email,
            role: profile.role,
            approvalStatus: profile.approvalStatus,
            active: profile.active,
          }
        : null,
      authCurrentUser: auth.currentUser
        ? {
            uid: auth.currentUser.uid,
            email: auth.currentUser.email,
          }
        : null,
    });
  }, [isAuthenticated, canValidateCheckin, profile, logValidationStep]);

  useEffect(() => {
    logValidationStep("runtime-context", {
      href: window.location.href,
      origin: window.location.origin,
      userAgent: navigator.userAgent,
      isSecureContext: window.isSecureContext,
      online: navigator.onLine,
    });
  }, [logValidationStep]);

  useEffect(() => {
    logValidationStep("session-load-observer", {
      sessionCode,
      loadingSession,
      sessionError,
      session: session
        ? {
            code: session.code,
            active: session.active,
            eventId: session.eventId,
            eventTitle: session.eventTitle,
            organizerId: session.organizerId,
          }
        : null,
    });
  }, [sessionCode, loadingSession, sessionError, session, logValidationStep]);

  useEffect(() => {
    if (!sessionCode) {
      setSessionError("Código de sessão inválido.");
      setLoadingSession(false);
      return;
    }

    get(ref(rtdb, `checkinSessions/${sessionCode}`))
      .then((snap) => {
        if (!snap.exists()) {
          setSessionError("Código não encontrado.");
          return;
        }

        const currentSession = snap.val();

        if (!currentSession.active) {
          setSessionError("Esta sessão foi desativada.");
          return;
        }

        setSession(currentSession);
      })
      .catch(() => setSessionError("Erro ao carregar sessão."))
      .finally(() => setLoadingSession(false));
  }, [sessionCode]);

  const stopScanner = useCallback(async () => {
    scannerRunIdRef.current += 1;
    startingRef.current = false;

    const scanner = qrRef.current;
    const wasStarted = startedRef.current;

    qrRef.current = null;
    startedRef.current = false;

    try {
      if (scanner && wasStarted) {
        await scanner.stop();
      }
    } catch (_) {}

    try {
      if (scanner) {
        await scanner.clear();
      }
    } catch (_) {}
  }, []);

  async function processCode(raw) {
    setScanError("");
    setTicket(null);
    setValidationResult(null);

    const parsed = parseQR(raw);
    activeScanRef.current = parsed;

    logValidationStep("process-code-start", {
      raw,
      parsed,
      session: session
        ? {
            code: session.code,
            eventId: session.eventId,
            organizerId: session.organizerId,
          }
        : null,
    });

    if (!parsed) {
      logValidationStep("process-code-invalid", { raw }, "warn");
      setScanError("QR inválido ou não pertence à OnurbPass.");
      setReviewing(true);
      return;
    }

    if (session && parsed.eventId !== session.eventId) {
      logValidationStep(
        "process-code-wrong-event",
        {
          parsedEventId: parsed.eventId,
          sessionEventId: session.eventId,
        },
        "warn"
      );
      setScanError("Ingresso pertence a outro evento.");
      setReviewing(true);
      return;
    }

    setFetchingTicket(true);
    setReviewing(true);

    try {
      const snap = await get(ref(rtdb, `tickets/${parsed.ticketId}`));

      if (!snap.exists()) {
        logValidationStep("ticket-fetch-missing", { ticketId: parsed.ticketId }, "warn");
        setScanError("Ingresso não encontrado no sistema.");
      } else {
        const ticketData = snap.val();

        logValidationStep("ticket-fetch-success", {
          ticketId: parsed.ticketId,
          ticket: ticketData,
        });

        setTicket({
          ...ticketData,
          ticketId: ticketData?.ticketId || parsed.ticketId,
          organizerId: ticketData?.organizerId || session?.organizerId || null,
          qrValue: ticketData?.qrValue || String(raw).trim(),
        });
      }
    } catch (err) {
      logValidationStep(
        "ticket-fetch-error",
        { ticketId: parsed.ticketId, error: err },
        "error"
      );
      setScanError("Erro ao buscar ingresso. Tente novamente.");
    } finally {
      setFetchingTicket(false);
    }
  }

  const handleScanned = useCallback(
    async (raw) => {
      if (reviewingRef.current) return;
      if (raw === lastScanRef.current) return;

      logValidationStep("camera-scan-detected", { raw });
      lastScanRef.current = raw;
      reviewingRef.current = true;

      await stopScanner();
      await processCode(raw);
    },
    [logValidationStep, stopScanner]
  );

  const startScanner = useCallback(async () => {
    if (!session || startingRef.current || startedRef.current) return;

    const bootstrapError = getCameraBootstrapError();
    if (bootstrapError) {
      setCameraError(bootstrapError);
      return;
    }

    const runId = scannerRunIdRef.current + 1;
    scannerRunIdRef.current = runId;
    startingRef.current = true;
    lastScanRef.current = "";
    setCameraError("");

    await new Promise((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(resolve))
    );

    const el = document.getElementById(regionId);
    if (!el) {
      startingRef.current = false;
      return;
    }

    const qrboxFn = (w, h) => {
      const size = Math.min(Math.floor(Math.min(w, h) * 0.8), 280);
      return { width: size, height: size };
    };

    const config = { fps: 10, qrbox: qrboxFn, disableFlip: false };
    const candidates = await getPreferredCameraCandidates();
    const attempts = [];
    const seen = new Set();

    const pushAttempt = (label, value) => {
      const key = typeof value === "string" ? `id:${value}` : JSON.stringify(value);
      if (seen.has(key)) return;
      seen.add(key);
      attempts.push({ label, value });
    };

    candidates.forEach((camera, index) => {
      const label = camera.label?.trim() || `Câmera ${index + 1}`;
      pushAttempt(label, camera.id);
    });

    pushAttempt("Ambiente (ideal)", { facingMode: { ideal: "environment" } });
    pushAttempt("Ambiente", { facingMode: "environment" });
    pushAttempt("Frontal", { facingMode: "user" });

    let lastError = null;

    for (const attempt of attempts) {
      if (startedRef.current) break;

      try {
        const scanner = new Html5Qrcode(regionId, { verbose: false });

        await scanner.start(
          attempt.value,
          config,
          (decoded) => handleScanned(decoded),
          () => {}
        );

        if (scannerRunIdRef.current !== runId) {
          try {
            await scanner.stop();
          } catch (_) {}

          try {
            await scanner.clear();
          } catch (_) {}

          return;
        }

        qrRef.current = scanner;
        startedRef.current = true;
      } catch (err) {
        lastError = err;
        console.warn("Camera attempt failed:", attempt.label, err?.message ?? err);
      }
    }

    if (!startedRef.current) {
      setCameraError(
        `${describeCameraError(lastError)} ` +
          "Verifique as permissões do navegador ou use o botão abaixo para fotografar o QR Code."
      );
    }

    if (scannerRunIdRef.current === runId) {
      startingRef.current = false;
    }
  }, [regionId, session, handleScanned]);

  useEffect(() => {
    if (!session) return undefined;

    (async () => {
      if (mode !== "camera") {
        await stopScanner();
        return;
      }

      if (!reviewing) {
        await startScanner();
      }
    })();

    return () => {
      stopScanner();
    };
  }, [mode, session, reviewing, startScanner, stopScanner]);

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, [stopScanner]);

  function handleManualSubmit() {
    const raw = manualCode.trim();
    if (!raw) return;

    logValidationStep("manual-submit", { raw });
    reviewingRef.current = true;
    processCode(raw);
  }

  async function handleFileCapture(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    logValidationStep("file-capture-selected", {
      name: file.name,
      size: file.size,
      type: file.type,
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    reviewingRef.current = true;
    const instanceId = `qr-file-${Date.now()}`;
    const el = document.createElement("div");
    el.id = instanceId;
    el.style.display = "none";
    document.body.appendChild(el);

    try {
      const qr = new Html5Qrcode(instanceId);
      const decoded = await qr.scanFile(file, true);
      logValidationStep("file-capture-decoded", { decoded });
      await processCode(decoded);
    } catch (err) {
      logValidationStep("file-capture-error", { error: err }, "error");
      setScanError("QR Code não encontrado na imagem. Tente novamente mais próximo.");
      setReviewing(true);
      reviewingRef.current = true;
    } finally {
      document.getElementById(instanceId)?.remove();
    }
  }

  function backToScanner() {
    setReviewing(false);
    setTicket(null);
    setScanError("");
    setValidationResult(null);
    setManualCode("");
    setCameraError("");
    activeScanRef.current = null;
    reviewingRef.current = false;
    lastScanRef.current = "";
  }

  async function handleValidate() {
    if (!ticket || validating) return;

    logValidationStep("validate-click", {
      ticket,
      activeScan: activeScanRef.current,
      authCurrentUser: auth.currentUser
        ? {
            uid: auth.currentUser.uid,
            email: auth.currentUser.email,
          }
        : null,
      profile: profile
        ? {
            uid: profile.uid,
            email: profile.email,
            role: profile.role,
            approvalStatus: profile.approvalStatus,
            active: profile.active,
          }
        : null,
      canValidateCheckin,
      session: session
        ? {
            code: session.code,
            eventId: session.eventId,
            organizerId: session.organizerId,
          }
        : null,
    });

    if (!canValidateCheckin) {
      logValidationStep(
        "validate-blocked-before-transaction",
        { reason: "canValidateCheckin_false" },
        "warn"
      );
      setValidationResult("auth_required");
      return;
    }

    setValidating(true);
    const resolvedTicketId = activeScanRef.current?.ticketId || ticket.ticketId;

    if (!resolvedTicketId) {
      logValidationStep(
        "validate-missing-ticket-id",
        { ticket, activeScan: activeScanRef.current },
        "error"
      );
      setValidationResult("error");
      setValidating(false);
      return;
    }

    try {
      let transactionCalls = 0;
      const expectedOrganizerId = session?.organizerId || auth.currentUser?.uid || null;
      const prefetchedTicket = ticket?.ticketId === resolvedTicketId ? ticket : null;

      logValidationStep("transaction-start", {
        resolvedTicketId,
        ticketStatus: ticket.status,
        expectedOrganizerId,
        prefetchedTicket,
      });

      const result = await runTransaction(
        ref(rtdb, `tickets/${resolvedTicketId}`),
        (current) => {
          transactionCalls += 1;

          logValidationStep("transaction-callback", {
            call: transactionCalls,
            resolvedTicketId,
            current,
          });

          if (!current && prefetchedTicket) {
            logValidationStep(
              "transaction-using-prefetched-ticket",
              {
                resolvedTicketId,
                prefetchedTicket,
              },
              "warn"
            );
          }

          const baseTicket = current || prefetchedTicket;

          if (!baseTicket) return undefined;
          if (baseTicket.status !== "valid") return undefined;
          if (session?.eventId && baseTicket.eventId !== session.eventId) return undefined;

          return {
            ...baseTicket,
            ticketId: baseTicket.ticketId || resolvedTicketId,
            organizerId: baseTicket.organizerId || expectedOrganizerId,
            status: "used",
            checkinAt: Date.now(),
            checkinByUid: auth.currentUser?.uid || null,
            checkinSessionCode: sessionCode,
          };
        }
      );

      logValidationStep("transaction-result", {
        resolvedTicketId,
        committed: result.committed,
        snapshot: result.snapshot?.val?.(),
      });

      if (result.committed) {
        const nextTicket = result.snapshot.val();

        logValidationStep("transaction-committed-ticket", {
          resolvedTicketId,
          nextTicket,
        });

        setTicket({
          ...nextTicket,
          ticketId: nextTicket?.ticketId || resolvedTicketId,
          organizerId: nextTicket?.organizerId || expectedOrganizerId,
        });
        setValidationResult("success");
      } else {
        const snap = await get(ref(rtdb, `tickets/${resolvedTicketId}`));

        logValidationStep(
          "transaction-abort-followup",
          {
            resolvedTicketId,
            exists: snap.exists(),
            snapshot: snap.exists() ? snap.val() : null,
          },
          "warn"
        );

        if (!snap.exists()) {
          setValidationResult("error");
        } else {
          const freshTicket = snap.val();
          const hydratedTicket = {
            ...freshTicket,
            ticketId: freshTicket?.ticketId || resolvedTicketId,
            organizerId: freshTicket?.organizerId || expectedOrganizerId,
          };

          setTicket(hydratedTicket);

          if (hydratedTicket.status === "used") {
            setValidationResult("already_used");
          } else if (hydratedTicket.status === "cancelled") {
            setValidationResult("cancelled");
          } else {
            setValidationResult("error");
          }
        }
      }
    } catch (err) {
      logValidationStep(
        "transaction-error",
        {
          resolvedTicketId,
          error: err,
          errorCode: err?.code ?? null,
          errorMessage: err?.message ?? null,
          authCurrentUser: auth.currentUser
            ? {
                uid: auth.currentUser.uid,
                email: auth.currentUser.email,
              }
            : null,
          profile: profile
            ? {
                uid: profile.uid,
                email: profile.email,
                role: profile.role,
                approvalStatus: profile.approvalStatus,
                active: profile.active,
              }
            : null,
        },
        "error"
      );

      console.error(err);

      const errCode = String(err?.code ?? err?.message ?? "").toLowerCase();
      if (
        errCode.includes("permission") ||
        errCode.includes("denied") ||
        errCode.includes("unauthorized") ||
        errCode.includes("auth")
      ) {
        setValidationResult("auth_required");
      } else {
        setValidationResult("error");
      }
    } finally {
      setValidating(false);
    }
  }

  if (loadingSession) {
    return (
      <Box sx={{ ...bgSx, display: "grid", placeItems: "center" }}>
        <CircularProgress sx={{ color: "#22c55e" }} />
      </Box>
    );
  }

  if (sessionError) {
    return (
      <Box sx={{ ...bgSx, display: "grid", placeItems: "center", px: 2 }}>
        <Paper sx={{ ...glassSx, maxWidth: 400, textAlign: "center" }}>
          <CancelRoundedIcon sx={{ fontSize: 48, color: "#f87171", mb: 1 }} />
          <Typography sx={{ fontWeight: 900, fontSize: 20, mb: 0.5 }}>
            Sessão inválida
          </Typography>
          <Typography sx={{ opacity: 0.65, mb: 2.5 }}>{sessionError}</Typography>
          <Button variant="outlined" sx={ghostBtn} onClick={() => navigate("/checkin")}>
            Voltar ao início
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={bgSx}>
      <Box sx={{ maxWidth: 700, mx: "auto", p: { xs: 2, sm: 3 } }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ mb: 2.5 }}
        >
          <Box>
            <Typography
              sx={{
                opacity: 0.55,
                fontSize: 12,
                letterSpacing: "0.12em",
                fontWeight: 700,
                mb: 0.3,
              }}
            >
              CHECK-IN
            </Typography>
            <Typography sx={{ fontWeight: 900, fontSize: { xs: 20, sm: 26 } }}>
              {session?.eventTitle || "Evento"}
            </Typography>
            <Chip
              size="small"
              label={`Sessão: ${sessionCode}`}
              sx={{
                mt: 0.5,
                fontFamily: "monospace",
                fontWeight: 900,
                background: "rgba(34,197,94,0.12)",
                border: "1px solid rgba(34,197,94,0.3)",
                color: "#4ade80",
              }}
            />
          </Box>

          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBackRoundedIcon />}
            sx={ghostBtn}
            onClick={() => navigate("/checkin")}
          >
            Sair
          </Button>
        </Stack>

        {reviewing ? (
          <ReviewPanel
            fetchingTicket={fetchingTicket}
            scanError={scanError}
            ticket={ticket}
            validating={validating}
            validationResult={validationResult}
            isAuthenticated={isAuthenticated}
            canValidateCheckin={canValidateCheckin}
            profile={profile}
            onValidate={handleValidate}
            onBack={backToScanner}
            onLogin={() =>
              navigate("/login", {
                state: { from: { pathname: `/checkin/scanner/${sessionCode}` } },
              })
            }
          />
        ) : (
          <Paper sx={glassSx} elevation={0}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
              <Button
                fullWidth
                variant={mode === "camera" ? "contained" : "outlined"}
                startIcon={<QrCodeScannerRoundedIcon />}
                sx={mode === "camera" ? greenBtn : ghostBtn}
                onClick={() => setMode("camera")}
              >
                Usar câmera
              </Button>
              <Button
                fullWidth
                variant={mode === "manual" ? "contained" : "outlined"}
                startIcon={<KeyboardRoundedIcon />}
                sx={mode === "manual" ? greenBtn : ghostBtn}
                onClick={() => setMode("manual")}
              >
                Digitar código
              </Button>
            </Stack>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mb: 2 }} />

            {mode === "camera" && (
              <Stack spacing={1.5}>
                {!cameraError && (
                  <Box
                    sx={{
                      borderRadius: "14px",
                      overflow: "hidden",
                      border: "1px solid rgba(255,255,255,0.10)",
                      background: "rgba(0,0,0,0.3)",
                      minHeight: 280,
                    }}
                  >
                    <Box id={regionId} sx={{ width: "100%" }} />
                  </Box>
                )}

                {cameraError ? (
                  <Stack spacing={1.5}>
                    <Alert
                      severity="warning"
                      sx={{
                        borderRadius: "12px",
                        background: "rgba(251,191,36,0.10)",
                        border: "1px solid rgba(251,191,36,0.25)",
                        color: "#fde68a",
                        "& .MuiAlert-icon": { color: "#fbbf24" },
                      }}
                    >
                      {cameraError}
                    </Alert>

                    <Box>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        style={{ display: "none" }}
                        onChange={handleFileCapture}
                      />
                      <Button
                        fullWidth
                        variant="contained"
                        sx={greenBtn}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        📷 Fotografar QR Code
                      </Button>
                    </Box>

                    <Button
                      variant="outlined"
                      sx={ghostBtn}
                      size="small"
                      onClick={startScanner}
                    >
                      Tentar câmera novamente
                    </Button>
                  </Stack>
                ) : (
                  <>
                    <Typography sx={{ opacity: 0.65, fontSize: 13, textAlign: "center" }}>
                      Aponte a câmera para o QR Code do ingresso
                    </Typography>

                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        sx={ghostBtn}
                        size="small"
                        fullWidth
                        onClick={startScanner}
                      >
                        Reabrir câmera
                      </Button>

                      <Button
                        variant="outlined"
                        sx={ghostBtn}
                        size="small"
                        fullWidth
                        onClick={() => fileInputRef.current?.click()}
                      >
                        📷 Foto
                      </Button>

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        style={{ display: "none" }}
                        onChange={handleFileCapture}
                      />
                    </Stack>
                  </>
                )}
              </Stack>
            )}

            {mode === "manual" && (
              <Stack spacing={1.5}>
                <Typography sx={{ fontWeight: 800 }}>
                  Cole ou digite o código do ingresso
                </Typography>
                <TextField
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleManualSubmit()}
                  placeholder="ONURB|tix_...|ord_...|eventId"
                  fullWidth
                  multiline
                  rows={2}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "14px",
                      background: "rgba(0,0,0,0.28)",
                      fontFamily: "monospace",
                      fontSize: 12,
                      "& fieldset": { borderColor: "rgba(255,255,255,0.14)" },
                    },
                  }}
                />
                <Button
                  variant="contained"
                  sx={greenBtn}
                  onClick={handleManualSubmit}
                  disabled={!manualCode.trim()}
                >
                  Buscar ingresso
                </Button>
              </Stack>
            )}
          </Paper>
        )}
      </Box>
    </Box>
  );
}

function ReviewPanel({
  fetchingTicket,
  scanError,
  ticket,
  validating,
  validationResult,
  isAuthenticated,
  canValidateCheckin,
  profile,
  onValidate,
  onBack,
  onLogin,
}) {
  const localGlassSx = {
    p: { xs: 2, sm: 3 },
    borderRadius: "20px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.10)",
    backdropFilter: "blur(18px)",
  };

  const localGhostBtn = {
    borderRadius: "14px",
    color: "rgba(255,255,255,0.88)",
    borderColor: "rgba(255,255,255,0.18)",
    fontWeight: 700,
  };

  const localGreenBtn = {
    background: "#22c55e",
    "&:hover": { background: "#16a34a" },
    borderRadius: "14px",
    fontWeight: 900,
    color: "#052e16",
  };

  const localRedBtn = {
    borderRadius: "14px",
    borderColor: "rgba(248,113,113,0.4)",
    color: "#fca5a5",
    fontWeight: 700,
    "&:hover": {
      borderColor: "#f87171",
      background: "rgba(248,113,113,0.08)",
    },
  };

  if (validationResult === "success") {
    return (
      <Paper
        sx={{
          ...localGlassSx,
          textAlign: "center",
          border: "1px solid rgba(34,197,94,0.35)",
          background: "rgba(34,197,94,0.06)",
        }}
      >
        <CheckCircleRoundedIcon sx={{ fontSize: 64, color: "#22c55e", mb: 1.5 }} />
        <Typography sx={{ fontWeight: 950, fontSize: 26, mb: 0.5 }}>
          Entrada liberada!
        </Typography>
        <Typography sx={{ opacity: 0.7, mb: 0.5 }}>{ticket?.ticketTypeName}</Typography>
        <Typography sx={{ opacity: 0.55, fontSize: 13, mb: 3 }}>
          Check-in registrado em {fmtDate(ticket?.checkinAt)}
        </Typography>
        <Button variant="contained" sx={localGreenBtn} size="large" fullWidth onClick={onBack}>
          Próximo ingresso
        </Button>
      </Paper>
    );
  }

  if (validationResult === "already_used") {
    return (
      <Paper
        sx={{
          ...localGlassSx,
          textAlign: "center",
          border: "1px solid rgba(248,113,113,0.3)",
          background: "rgba(248,113,113,0.05)",
        }}
      >
        <CancelRoundedIcon sx={{ fontSize: 64, color: "#f87171", mb: 1.5 }} />
        <Typography sx={{ fontWeight: 950, fontSize: 24, mb: 0.5 }}>
          Ingresso já utilizado
        </Typography>
        <Typography sx={{ opacity: 0.65, mb: 0.5 }}>{ticket?.ticketTypeName}</Typography>
        {ticket?.checkinAt && (
          <Typography sx={{ opacity: 0.5, fontSize: 13, mb: 3 }}>
            Check-in realizado em {fmtDate(ticket?.checkinAt)}
          </Typography>
        )}
        <Button variant="outlined" sx={localGhostBtn} fullWidth onClick={onBack}>
          Voltar ao scanner
        </Button>
      </Paper>
    );
  }

  if (validationResult === "error") {
    return (
      <Paper sx={localGlassSx}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Erro ao validar. Tente novamente.
        </Alert>
        <Button variant="outlined" sx={localGhostBtn} onClick={onBack}>
          Voltar ao scanner
        </Button>
      </Paper>
    );
  }

  if (validationResult === "auth_required") {
    return (
      <Paper
        sx={{
          ...localGlassSx,
          border: "1px solid rgba(251,191,36,0.25)",
          background: "rgba(251,191,36,0.06)",
        }}
      >
        <Alert
          severity="warning"
          sx={{
            mb: 2,
            borderRadius: "12px",
            background: "rgba(251,191,36,0.10)",
            border: "1px solid rgba(251,191,36,0.25)",
            color: "#fde68a",
            "& .MuiAlert-icon": { color: "#fbbf24" },
          }}
        >
          {!isAuthenticated
            ? "Este dispositivo precisa estar autenticado para concluir a transaction do check-in."
            : !canValidateCheckin
              ? "A conta autenticada neste dispositivo não tem permissão de organizador aprovado para validar ingressos."
              : "A transaction do check-in foi bloqueada por permissão neste dispositivo."}
        </Alert>

        <Typography sx={{ opacity: 0.72, mb: 0.75 }}>
          O QR foi lido corretamente, mas a validação precisa de uma sessão autenticada no Firebase.
        </Typography>

        {isAuthenticated && (
          <Typography sx={{ opacity: 0.5, fontSize: 13, mb: 2 }}>
            Sessão atual: {profile?.email || profile?.name || "usuário autenticado"}.
          </Typography>
        )}

        <Stack spacing={1}>
          {!canValidateCheckin && (
            <Button fullWidth variant="contained" sx={localGreenBtn} onClick={onLogin}>
              Entrar com conta do organizador
            </Button>
          )}
          <Button variant="outlined" sx={localGhostBtn} fullWidth onClick={onBack}>
            Voltar ao scanner
          </Button>
        </Stack>
      </Paper>
    );
  }

  if (fetchingTicket) {
    return (
      <Paper sx={{ ...localGlassSx, display: "grid", placeItems: "center", py: 6 }}>
        <CircularProgress sx={{ color: "#22c55e" }} />
        <Typography sx={{ mt: 2, opacity: 0.65 }}>Buscando ingresso...</Typography>
      </Paper>
    );
  }

  if (scanError) {
    return (
      <Paper
        sx={{
          ...localGlassSx,
          border: "1px solid rgba(248,113,113,0.3)",
          background: "rgba(248,113,113,0.05)",
        }}
      >
        <CancelRoundedIcon sx={{ fontSize: 40, color: "#f87171", mb: 1.5 }} />
        <Typography sx={{ fontWeight: 900, fontSize: 18, mb: 0.5 }}>
          QR inválido
        </Typography>
        <Typography sx={{ opacity: 0.7, mb: 2.5 }}>{scanError}</Typography>
        <Button variant="outlined" sx={localGhostBtn} fullWidth onClick={onBack}>
          Voltar ao scanner
        </Button>
      </Paper>
    );
  }

  if (!ticket) return null;

  const isValid = ticket.status === "valid";
  const isUsed = ticket.status === "used";

  const statusColors = {
    valid: {
      chip: {
        background: "rgba(34,197,94,0.14)",
        border: "1px solid rgba(34,197,94,0.35)",
        color: "#4ade80",
      },
      label: "Válido",
    },
    used: {
      chip: {
        background: "rgba(248,113,113,0.12)",
        border: "1px solid rgba(248,113,113,0.3)",
        color: "#f87171",
      },
      label: "Já utilizado",
    },
    cancelled: {
      chip: {
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.14)",
        color: "rgba(255,255,255,0.6)",
      },
      label: "Cancelado",
    },
  };

  const sc = statusColors[ticket.status] || statusColors.cancelled;

  return (
    <Paper sx={localGlassSx} elevation={0}>
      <Box
        sx={{
          p: 1.5,
          mb: 2.5,
          borderRadius: "14px",
          border: sc.chip.border,
          background: sc.chip.background,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        {isValid ? (
          <CheckCircleRoundedIcon sx={{ color: "#4ade80", fontSize: 28 }} />
        ) : (
          <CancelRoundedIcon
            sx={{ color: isUsed ? "#f87171" : "rgba(255,255,255,0.5)", fontSize: 28 }}
          />
        )}

        <Box>
          <Typography sx={{ fontWeight: 950, fontSize: 17, color: sc.chip.color }}>
            {sc.label}
          </Typography>
          {isUsed && ticket.checkinAt && (
            <Typography sx={{ fontSize: 12, opacity: 0.7 }}>
              Check-in em {fmtDate(ticket.checkinAt)}
            </Typography>
          )}
        </Box>
      </Box>

      <Stack spacing={1.2} sx={{ mb: 2.5 }}>
        <InfoRow
          icon={<ConfirmationNumberRoundedIcon sx={{ fontSize: 18 }} />}
          label="Tipo"
          value={ticket.ticketTypeName}
        />
        <InfoRow
          icon={<ConfirmationNumberRoundedIcon sx={{ fontSize: 18 }} />}
          label="Lote"
          value={ticket.lotName}
        />
        <InfoRow
          icon={<EventRoundedIcon sx={{ fontSize: 18 }} />}
          label="Evento"
          value={ticket.eventTitle}
        />
        {(ticket.eventCity || ticket.eventState) && (
          <InfoRow
            icon={<PlaceRoundedIcon sx={{ fontSize: 18 }} />}
            label="Local"
            value={`${ticket.eventCity || ""}${ticket.eventCity && ticket.eventState ? " · " : ""}${ticket.eventState || ""}`}
          />
        )}
        <InfoRow icon={null} label="Valor pago" value={fmt(ticket.totalPaid)} highlight />
        <InfoRow icon={null} label="Comprado em" value={fmtDate(ticket.purchasedAt)} />

        <Box
          sx={{
            p: 1,
            borderRadius: "10px",
            background: "rgba(0,0,0,0.25)",
            fontFamily: "monospace",
            fontSize: 10,
            opacity: 0.55,
            wordBreak: "break-all",
          }}
        >
          {ticket.qrValue}
        </Box>
      </Stack>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mb: 2 }} />

      {isValid ? (
        <Stack spacing={1}>
          {!canValidateCheckin && (
            <Alert
              severity="warning"
              sx={{
                borderRadius: "12px",
                background: "rgba(251,191,36,0.10)",
                border: "1px solid rgba(251,191,36,0.25)",
                color: "#fde68a",
                "& .MuiAlert-icon": { color: "#fbbf24" },
              }}
            >
              {!isAuthenticated
                ? "Faça login neste dispositivo antes de validar. A escrita do check-in continua sendo feita por transaction."
                : "A conta atual não possui permissão de organizador aprovado para validar este ingresso."}
            </Alert>
          )}

          {!canValidateCheckin && (
            <Button fullWidth variant="outlined" sx={localGhostBtn} onClick={onLogin}>
              Entrar com conta do organizador
            </Button>
          )}

          <Button
            fullWidth
            size="large"
            variant="contained"
            sx={localGreenBtn}
            onClick={onValidate}
            disabled={validating || !canValidateCheckin}
            startIcon={
              validating ? (
                <CircularProgress size={18} sx={{ color: "#052e16" }} />
              ) : (
                <CheckCircleRoundedIcon />
              )
            }
          >
            {validating ? "Validando..." : "Validar entrada"}
          </Button>

          <Button
            fullWidth
            variant="outlined"
            sx={localRedBtn}
            onClick={onBack}
            disabled={validating}
          >
            Cancelar
          </Button>
        </Stack>
      ) : (
        <Button fullWidth variant="outlined" sx={localGhostBtn} onClick={onBack}>
          Voltar ao scanner
        </Button>
      )}
    </Paper>
  );
}

function InfoRow({ icon, label, value, highlight }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
      <Stack
        direction="row"
        spacing={0.8}
        alignItems="center"
        sx={{ opacity: 0.6, flexShrink: 0 }}
      >
        {icon}
        <Typography sx={{ fontSize: 13 }}>{label}</Typography>
      </Stack>
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: highlight ? 950 : 700,
          color: highlight ? "#4ade80" : "rgba(255,255,255,0.9)",
          textAlign: "right",
        }}
      >
        {value || "—"}
      </Typography>
    </Box>
  );
}